import { jwtDecode } from "jwt-decode";
import type {
    IdTokenClaims,
    RefreshState,
    SigninResponse,
} from "oidc-client-ts";
import { Log, OidcClient } from "oidc-client-ts";
import * as v from "valibot";
import { TARGET_API_V2 } from "../../../managers";
import { logger, LOGS } from "../../../utils";
import { OAUTH_CONFIG, OAuthConfig } from "../config/KeycloakOAuthConfig";
import { AuthUser, BrokeredProvider } from "../types/auth";
import { AuthSession } from "./AuthSession";
import { TokenRefreshFailedError } from "./errors";
import { IOAuthService } from "./IOAuthService";
import { KeycloakOAuthShared, KeycloakTokens } from "./KeycloakOAuthShared";

/**
 * Strict schema for the token fields consumed from the OIDC token response. Every field is required:
 * a login or refresh that does not return all three tokens (plus the access token expiry) is treated
 * as a failure rather than silently falling back to stale state.
 */
const TOKEN_RESPONSE_SCHEMA = v.object({
    access_token: v.pipe(
        v.string(),
        v.description("The Keycloak access token."),
    ),
    expires_at: v.pipe(
        v.number(),
        v.description("Access token expiry, in seconds since epoch."),
    ),
    id_token: v.pipe(v.string(), v.description("The Keycloak id token.")),
    refresh_token: v.pipe(
        v.string(),
        v.description("The Keycloak refresh token."),
    ),
});

/**
 * Binds the OIDC client's logging to our application's logging system, tagging all logs with the KeycloakAuth tag.
 */
Log.setLogger({
    info: (...args) => logger.info([LOGS.KeycloakAuth], args.join(" ")),
    debug: (...args) => logger.info([LOGS.KeycloakAuth], args.join(" ")),
    warn: (...args) => logger.warn([LOGS.KeycloakAuth], args.join(" ")),
    error: (...args) => logger.error([LOGS.KeycloakAuth], args.join(" ")),
});

/**
 * Configures the OIDC client logging level.
 */
Log.setLevel(Log.DEBUG);

class KeycloakOAuthService implements IOAuthService {
    /** In-flight operations de-duplicated by key, so concurrent callers share a single promise. */
    private readonly inFlight = new Map<string, Promise<unknown>>();

    /** The current authentication session: tokens, user and provider info when authenticated. */
    private readonly session = new AuthSession();

    /** The identity provider Keycloak brokers for this service. */
    private readonly brokeredProvider = BrokeredProvider.Microsoft;

    private readonly oidcClient: OidcClient;

    public constructor(private readonly config: OAuthConfig) {
        this.oidcClient = new OidcClient({
            authority: this.config.authority,
            metadata: {
                issuer: this.config.authority,
                authorization_endpoint: `${this.config.authority}/protocol/openid-connect/auth`,
                token_endpoint: `${this.config.authority}/protocol/openid-connect/token`,
                userinfo_endpoint: `${this.config.authority}/protocol/openid-connect/userinfo`,
                end_session_endpoint: `${this.config.authority}/protocol/openid-connect/logout`,
            },
            client_id: this.config.clientId,
            redirect_uri: this._buildAbsoluteUri(this.config.redirectUri),
            post_logout_redirect_uri: this._buildAbsoluteUri(
                this.config.postLogoutRedirectUri || "/",
            ),
            response_type: "code",
            response_mode: "fragment",
            scope: this.config.scopes.join(" "),

            disablePKCE: false,

            filterProtocolClaims: true,
            loadUserInfo: true,
        });
    }

    public login(): Promise<void> {
        throw new Error("unimplemented");
    }

    public logout(): Promise<void> {
        throw new Error("unimplemented");
    }

    public loginRedirect(): Promise<never> {
        return KeycloakOAuthShared.dedup(
            this.inFlight,
            "login-redirect",
            async (): Promise<never> => {
                logger.info([LOGS.KeycloakAuth], "Initiating login redirect");
                // Builds the authorize URL and persists the PKCE state; navigating is up to us.
                const request = await this.oidcClient.createSigninRequest({});
                globalThis.location.assign(request.url);
                throw new Error("Should have been redirected");
            },
        );
    }

    public logoutRedirect(): Promise<never> {
        return KeycloakOAuthShared.dedup(
            this.inFlight,
            "logout-redirect",
            async (): Promise<never> => {
                logger.info([LOGS.KeycloakAuth], "Initiating logout redirect");
                const idTokenHint = this.session.peekKeycloakIdToken();
                await this._signoutRedirect(idTokenHint);
                throw new Error("Should have been redirected");
            },
        );
    }

    public signOut(): Promise<never> {
        return this.logoutRedirect();
    }

    public loginCallback(): Promise<void> {
        return KeycloakOAuthShared.dedup(
            this.inFlight,
            "login-callback",
            async (): Promise<void> => {
                logger.info([LOGS.KeycloakAuth], "Processing login callback");

                if (!this._isKeycloakCallback()) {
                    await this.oidcClient.clearStaleState();
                    await this.loginRedirect();
                    return;
                }

                const response = await this._doLoginCallback();

                try {
                    await this._completeLogin(response);
                } catch (e) {
                    logger.error(
                        [LOGS.KeycloakAuth],
                        "Login callback error:" + e,
                    );
                    await this._signoutRedirect(response.id_token);
                }
                logger.info(
                    [LOGS.KeycloakAuth],
                    "Login callback processing complete",
                );
            },
        );
    }

    private async _doLoginCallback(): Promise<SigninResponse> {
        try {
            const url = globalThis.location.href;
            this._removeUrlFragment();
            // Reads the authorization code from the URL, matches it against the stored PKCE state and
            // exchanges it at the token endpoint (plus the userinfo call, via `loadUserInfo`).
            return await this.oidcClient.processSigninResponse(url);
        } catch {
            await this.loginRedirect();
            throw new Error("Should have been redirected");
        }
    }

    /**
     * Builds the end-session URL and navigates to it. Mirrors what `UserManager.signoutRedirect` used
     * to do for us, now that the bare {@link OidcClient} only produces the request.
     * @param idTokenHint The id token of the session being closed, passed to Keycloak as `id_token_hint`.
     * @private
     */
    private async _signoutRedirect(
        idTokenHint: string | undefined,
    ): Promise<void> {
        const request = await this.oidcClient.createSignoutRequest({
            id_token_hint: idTokenHint,
        });
        globalThis.location.assign(request.url);
    }

    public refreshTokens(): Promise<void> {
        return KeycloakOAuthShared.dedup(
            this.inFlight,
            "refresh-tokens",
            async (): Promise<void> => {
                if (!this.session.isAuthenticated()) {
                    throw new TokenRefreshFailedError(
                        "refreshTokens called while not authenticated",
                    );
                }
                try {
                    const response = await this.oidcClient.useRefreshToken({
                        state: this._buildRefreshState(),
                    });
                    await this._completeLogin(response);
                } catch (e) {
                    // Aucun effet de bord de déconnexion : on signale l'échec via une erreur typée, qu'AuthProvider
                    // intercepte pour émettre auth-token-rejected (cleanup services → signOut = logoutRedirect).
                    logger.error(
                        [LOGS.KeycloakAuth],
                        "Refresh token error:" + e,
                    );
                    throw e instanceof TokenRefreshFailedError
                        ? e
                        : new TokenRefreshFailedError(String(e), { cause: e });
                }
            },
        );
    }

    public async getAccessToken(): Promise<string> {
        if (this.session.areTokensExpired()) {
            await this.refreshTokens();
        }
        return this.session.getKeycloakAccessTokenValue();
    }

    public async getProviderToken(): Promise<string> {
        if (this.session.areTokensExpired()) {
            await this.refreshTokens();
        }
        return this.session.getProviderAccessTokenValue();
    }

    public getBrokeredProvider(): BrokeredProvider {
        return this.session.getBrokeredProvider();
    }

    public isAuthenticated(): boolean {
        return this.session.isAuthenticated();
    }

    /**
     * Completes login/refresh from the OIDC token response: extracts the Keycloak tokens, exchanges
     * the access token for the brokered provider token and external id, builds the {@link AuthUser},
     * and stores the resulting authenticated session.
     * @param response The token response returned by the OIDC client after a successful login or refresh.
     * @private
     */
    private async _completeLogin(response: SigninResponse): Promise<void> {
        const tokens = this._extractKeycloakTokens(response);

        const { providerToken, externalId } =
            await KeycloakOAuthShared.performTokenExchange(
                tokens.keycloakAccessToken.value,
                this.config.authority,
                this.brokeredProvider,
                this.session.peekProviderAccessToken(),
            );

        this.session.setAuthenticated({
            status: "authenticated",
            ...tokens,
            providerAccessToken: providerToken,
            authUser: KeycloakOAuthShared.buildAuthUser(
                response.profile,
                externalId,
            ),
            brokeredProvider: this.brokeredProvider,
        });
    }

    /**
     * Checks if the current URL contains the parameters indicating a Keycloak login callback (code and state).
     * @private
     */
    private _isKeycloakCallback(): boolean {
        const params = new URLSearchParams(
            globalThis.location.hash.replace(/^#/, ""),
        );
        return params.has("code") && params.has("state");
    }

    /**
     * Removes the URL fragment (hash) from the current location,
     * typically used after processing the login callback to clean up the URL.
     * @private
     */
    private _removeUrlFragment(): void {
        globalThis.history.replaceState(
            {},
            document.title,
            globalThis.location.pathname + globalThis.location.search,
        );
    }

    /**
     * Builds the {@link RefreshState} the OIDC client needs to exchange the stored refresh token at the
     * token endpoint. The profile is decoded from the stored id token so the client can verify that the
     * subject of any id_token returned by the refresh matches the current session.
     * @private
     */
    private _buildRefreshState(): RefreshState {
        const refreshToken = this.session.peekKeycloakRefreshToken();
        const idToken = this.session.peekKeycloakIdToken();
        if (refreshToken === undefined || idToken === undefined) {
            throw new Error("User is not authenticated");
        }
        return {
            refresh_token: refreshToken,
            id_token: idToken,
            session_state: null,
            scope: this.config.scopes.join(" "),
            profile: jwtDecode<IdTokenClaims>(idToken),
        };
    }

    /**
     * Builds an absolute URI from the given relative path, using the application's origin.
     * @param relativePath
     * @private
     */
    private _buildAbsoluteUri(relativePath: string): string {
        if (!relativePath) return globalThis.location.origin;

        if (
            relativePath.startsWith("http://") ||
            relativePath.startsWith("https://")
        ) {
            return relativePath;
        }

        const origin = globalThis.location.origin;
        const path = relativePath.startsWith("/")
            ? relativePath
            : "/" + relativePath;

        return origin + path;
    }

    /**
     * Extracts the Keycloak access, id and refresh tokens from the OIDC token response, validating
     * against {@link TOKEN_RESPONSE_SCHEMA} that all three (and the access token expiry) are present.
     * @param response The token response returned by the OIDC client after a login or token refresh.
     * @private
     */
    private _extractKeycloakTokens(response: SigninResponse): KeycloakTokens {
        const { access_token, expires_at, id_token, refresh_token } = v.parse(
            TOKEN_RESPONSE_SCHEMA,
            response,
        );
        return {
            keycloakIdToken: id_token,
            keycloakAccessToken: {
                value: access_token,
                expiresAtMs: expires_at * 1000,
            },
            keycloakRefreshToken: refresh_token,
        };
    }

    public getUser(): AuthUser {
        return this.session.getUser();
    }

    /**
     * Exposes the auth session so the React layer can subscribe to authentication-state changes.
     */
    public getSession(): AuthSession {
        return this.session;
    }
}

let auth: KeycloakOAuthService | null = null;

interface MailvistaAuthConfig {
    apiTarget: TARGET_API_V2;
    publicUrls?: string[];
}

/**
 * Initializes Mailvista authentication service. Must be called before starting the app.
 * @param param0
 * @param param0.apiTarget - The API target environment.
 * @param param0.publicUrls - Optional list of URL path prefixes that should skip authentication initialization, allowing public access without redirecting to login.
 */
export async function initializeMailvistaAuth({
    apiTarget,
    publicUrls = [],
}: MailvistaAuthConfig): Promise<void> {
    if (auth) {
        return;
    }

    auth = new KeycloakOAuthService(OAUTH_CONFIG[apiTarget]);

    try {
        if (
            publicUrls.some((url) =>
                globalThis.location.pathname.startsWith(url),
            )
        ) {
            logger.info(
                [LOGS.KeycloakAuth],
                "Public URL detected, skipping authentication initialization",
            );
            return;
        }
        await auth.loginCallback();
    } catch (e) {
        logger.error(
            [LOGS.KeycloakAuth],
            "Authentication initialization error:" + e,
        );
    }
}

/**
 * Retrieves the initialized authentication service instance. Throws an error if the service has not been initialized yet.
 * @returns The KeycloakOAuthService instance used for authentication operations throughout the app.
 */
export function getAuthService(): KeycloakOAuthService {
    if (!auth) {
        throw new Error(
            "Auth service not initialized. Call initializeMailvistaAuth() first.",
        );
    }
    return auth;
}

/**
 * Retrieves the auth session of the initialized service, for React to subscribe to via
 * `useSyncExternalStore`. Throws if the service has not been initialized yet.
 */
export function getAuthSession(): AuthSession {
    return getAuthService().getSession();
}
